import { Button, Dropdown, MenuProps } from 'antd';
import { createSearchParams, useLocation, useSearchParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ACCESS_TOKEN_STORAGE_KEY,
  EXPIRED,
  NO_PROJECTS, REDIRECT_TO, REGION, SIGN_IN_INCOMPLETE, USER_INITIATED,
} from '../../config/constants';
import {
  Region, currentRegion, hostnameOptions,
} from '../../config';
import { notification } from '../../antdProvider';
import style from './styles.module.less';
import { Icon } from '../../components/atoms';
import { client } from '../../services';

const LoginView: React.FC = () => {
  const { loginWithRedirect } = useAuth0();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Set by RootRoute when a signed-in user has no project to open, as navigation state that a
  // link cannot set. Trusted only with project data loaded, which this page never loads itself,
  // so a reload goes through the app once more and is marked again.
  const noProjectToOpen = !!(location.state as Record<string, unknown> | null)?.[NO_PROJECTS]
    && !!client.cache.current.bootstrap;

  // A stored session is sent on to the app, unless the app sent it here because the user has
  // no project to open, which would only send it straight back.
  const [bounce] = useState(() => (
    !!localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY) && !noProjectToOpen
  ));

  const loginHandler = async () => {
    const redirectTo = searchParams.get(REDIRECT_TO) || '';
    await loginWithRedirect({
      authorizationParams: {
        redirectUri: `${window.location.origin}/auth0-callback`,
      },
      appState: {
        [REDIRECT_TO]: redirectTo,
      },
    });
  };

  const regionSelectHandler = (region: Region) => {
    localStorage.setItem(REGION, region);
    window.location.reload();
  };

  // const regionMap = keyBy(hostnameOptions, 'region');
  const regionMap = new Map(hostnameOptions.map(option => [option.region, option]));

  const handleUnauthenticatedReason = () => {
    const unauthenticatedReasonKey = 'unauthenticatedReason';
    if (searchParams.has(unauthenticatedReasonKey)) {
      const unauthenticatedReasonParam = searchParams.get(unauthenticatedReasonKey);
      switch (unauthenticatedReasonParam) {
        case EXPIRED:
          notification.warning({
            message: 'Session Expired',
            description: 'Enter your credentials to log into Ynomia.',
          });
          break;
        case USER_INITIATED:
          notification.info({
            message: 'Logged Out',
            description: 'You have been successfully logged out.',
          });
          break;
        case SIGN_IN_INCOMPLETE:
          notification.warning({
            message: 'Sign In Not Completed',
            description: 'Please try signing in again.',
          });
          break;
        default:
          break;
      }
      searchParams.delete(unauthenticatedReasonKey);
      setSearchParams(searchParams);
    }
  };

  const onClick: MenuProps['onClick'] = e => regionSelectHandler(e.key as Region);

  const items: MenuProps['items'] = hostnameOptions.map(item => (
    { label: item.label, key: item.region }
  ));

  useEffect(() => {
    handleUnauthenticatedReason();
    if (noProjectToOpen) {
      // Keyed, as effects can run twice
      notification.error({
        key: NO_PROJECTS,
        message: 'No projects to show!',
      });
    }
  }, []);

  useEffect(() => {
    if (!bounce) return;
    // A full page load, not an in-app move: the stored session may be newer than this page's
    // client (another tab or app signed in after this page loaded), and only a load reads it.
    // An in-app move would find this page's signed-out client and bounce between "/" and here.
    const redirectTo = searchParams.get(REDIRECT_TO);
    window.location.replace(
      redirectTo ? `/?${createSearchParams({ [REDIRECT_TO]: redirectTo })}` : '/',
    );
  }, []);

  if (bounce) return null;

  return (
    <div className={style.mainContainer}>
      <div className={style.container}>
        <div>
          <Icon name="ynomiaLight" className={style.logo} />
        </div>
        <div className={style.logoName}>YNOMIA</div>
        <div className={style.logoDesc}>REAL-TIME CONSTRUCTION</div>
        <Button
          id="login-button"
          type="primary"
          size="large"
          className={style.loginBtn}
          htmlType="submit"
          onClick={loginHandler}
        >
          Log In
        </Button>
        <a href="/forgot-password" className={style.forgotPwdBtn}>Forgot password?</a>

      </div>
      <Dropdown menu={{ onClick, items }} trigger={['click']}>
        <div
          onClick={e => e.preventDefault()}
          className={style.regionButton}
          role="button"
          tabIndex={0}
          style={{ cursor: 'pointer' }}
        >
          {regionMap.get(currentRegion)!.label}
        </div>
      </Dropdown>
    </div>
  );
};

export default LoginView;
